/**
 * Tooltip Host (React port of the Angular `TooltipComponent` + the imperative
 * rendering that `TooltipService` did via `createComponent`).
 *
 * Angular created a `TooltipComponent` on demand and appended it to a body
 * container, then positioned the `.tooltip-content` element. In React a single
 * host is mounted once (in providers); it subscribes to the tooltip store and
 * renders the floating `.tooltip-content` element with IDENTICAL markup,
 * classes, arrow, animation, and `data-position` handling.
 *
 * The store's positioning math (`computePlacement`) measures the live
 * `.tooltip-content` element, so this host always renders that element and
 * toggles visibility via the `tooltip--visible` class — mirroring the Angular
 * component, whose `visible` signal drove the same class.
 */

import { useTooltip } from '@/hooks/useTooltip';

import './Tooltip.scss';

/**
 * Renders the single, app-wide tooltip surface. Mount once near the app root.
 */
export function TooltipHost() {
  const { visible, content, maxWidth, placement } = useTooltip();

  const style: React.CSSProperties = {
    maxWidth: `${maxWidth}px`,
  };
  if (placement) {
    style.top = `${placement.top}px`;
    style.left = `${placement.left}px`;
  }

  return (
    <div
      role="tooltip"
      className={`tooltip-content${visible ? ' tooltip--visible' : ''}`}
      style={style}
      data-position={placement?.position}
    >
      <div className="tooltip__background" />
      <div className="tooltip__border" />
      <div className="tooltip__text">{content}</div>
      <div className="tooltip__arrow" data-arrow />
    </div>
  );
}

export default TooltipHost;
